import { useState, useEffect, useMemo } from 'preact/hooks';
import { getConfig } from '../config';
import { getAccount, updateAccount, getCountries, getStates } from '../api';
import { store } from '../store';
import { navigateToView } from '../navigation';
import { SearchableSelect } from './SearchableSelect';
import { t } from '../i18n';
import { ProfileFormSkeleton } from './LoadingSkeletons';
import { TelegramPanel } from './TelegramPanel';

export function ProfileView() {
  const config = useMemo(() => getConfig(), []);
  const [formData, setFormData] = useState({
    title: 'Mr.',
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    b_address: '',
    b_zipcode: '',
    b_city: '',
    b_country: '',
    b_state: '',
    same_as_billing: false,
    s_address: '',
    s_zipcode: '',
    s_city: '',
    s_country: '',
    s_state: '',
    passwd1: '',
    passwd2: '',
  });
  const [countries, setCountries] = useState<Record<string, string>>({});
  const [states, setStates] = useState<Record<string, Record<string, string>>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const loadData = async () => {
      try {
        const [accountRes, cRes, sRes] = await Promise.all([
          getAccount(config),
          getCountries(config),
          getStates(config)
        ]);

        if (cRes.status === 200 && cRes.result) setCountries(cRes.result);
        if (sRes.status === 200 && sRes.result) {
          const raw = sRes.result as Record<string, Record<string, string>> | Array<{ country_code: string; state_code: string; state: string }>;
          const byCountry: Record<string, Record<string, string>> = Array.isArray(raw)
            ? raw.reduce<Record<string, Record<string, string>>>((acc, row) => {
                const cc = row.country_code;
                if (!acc[cc]) acc[cc] = {};
                acc[cc][row.state_code] = row.state;
                return acc;
              }, {})
            : raw;
          setStates(byCountry);
        }

        if (accountRes.status === 200 && accountRes.result) {
          const data = accountRes.result as any;
          // Determine if shipping is same as billing based on data comparison
          const sameAsBilling = 
            data.s_address === data.b_address &&
            data.s_zipcode === data.b_zipcode &&
            data.s_city === data.b_city &&
            data.s_country === data.b_country &&
            data.s_state === data.b_state;

          setFormData({
            title: data.title || 'Mr.',
            firstname: data.firstname || '',
            lastname: data.lastname || '',
            email: data.email || '',
            phone: data.phone || '',
            b_address: data.b_address || '',
            b_zipcode: data.b_zipcode || '',
            b_city: data.b_city || '',
            b_country: data.b_country || '',
            b_state: data.b_state || '',
            same_as_billing: sameAsBilling,
            s_address: data.s_address || '',
            s_zipcode: data.s_zipcode || '',
            s_city: data.s_city || '',
            s_country: data.s_country || '',
            s_state: data.s_state || '',
            passwd1: '',
            passwd2: '',
          });
        }
      } catch (e) {
        setError(t('profile.loadFailed'));
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [config.apiUrl, config.siteKey]);

  const handleChange = (e: Event) => {
    const target = e.target as HTMLInputElement | HTMLSelectElement;
    const value = target.type === 'checkbox' ? (target as HTMLInputElement).checked : target.value;
    setFormData({ ...formData, [target.name]: value });
    if (validationErrors[target.name]) {
      setValidationErrors({ ...validationErrors, [target.name]: '' });
    }
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const errors: Record<string, string> = {};
    if (!formData.firstname) errors.firstname = t('profile.firstNameRequired');
    if (!formData.lastname) errors.lastname = t('profile.lastNameRequired');
    if (!formData.email) errors.email = t('profile.emailRequired');
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = t('profile.emailInvalid');
    else if (formData.email.length > 128) errors.email = t('profile.emailInvalid');
    if (!formData.phone) errors.phone = t('profile.phoneRequired');
    else if (formData.phone.length < 3 || formData.phone.length > 30) errors.phone = t('profile.phoneRequired');
    if (!formData.b_address) errors.b_address = t('profile.addressRequired');
    if (!formData.b_zipcode) errors.b_zipcode = t('profile.zipcodeRequired');
    if (!formData.b_city) errors.b_city = t('profile.cityRequired');
    if (!formData.b_country) errors.b_country = t('profile.countryRequired');
    
    if (!formData.same_as_billing) {
      if (!formData.s_address) errors.s_address = t('profile.addressRequired');
      if (!formData.s_zipcode) errors.s_zipcode = t('profile.zipcodeRequired');
      if (!formData.s_city) errors.s_city = t('profile.cityRequired');
      if (!formData.s_country) errors.s_country = t('profile.countryRequired');
    }

    if (formData.passwd1 || formData.passwd2) {
      if (formData.passwd1 !== formData.passwd2) errors.passwd2 = t('profile.passwordMismatch');
      if (formData.passwd1.length < 5) errors.passwd1 = t('profile.passwordMinLength');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validate()) return;

    setSaving(true);
    
    // Prepare data - if same_as_billing, copy billing to shipping
    const { passwd1, passwd2, ...profileFields } = formData;
    const submitData: Record<string, unknown> = { ...profileFields };
    if (passwd1) submitData.new_password = passwd1;
    if (submitData.same_as_billing) {
      submitData.s_address = submitData.b_address;
      submitData.s_zipcode = submitData.b_zipcode;
      submitData.s_city = submitData.b_city;
      submitData.s_country = submitData.b_country;
      submitData.s_state = submitData.b_state;
    }

    const res = await updateAccount(config, submitData);
    setSaving(false);

    if (res.status === 200) {
      setSuccess(t('profile.updateSuccess'));
      setFormData(prev => ({ ...prev, passwd1: '', passwd2: '' }));
      if (store.getReturnTo() === 'checkout-confirm') {
        store.setReturnTo(null);
        navigateToView('checkout-confirm');
      }
    } else {
      setError(res.error ?? t('profile.updateFailed'));
    }
  };

  const renderStateSelect = (countryCode: string, fieldName: string, value: string) => {
    const countryStates = states[countryCode];
    if (!countryStates || Object.keys(countryStates).length === 0) {
      return (
        <input
          type="text"
          class={`form-control ${validationErrors[fieldName] ? 'is-invalid' : ''}`}
          name={fieldName}
          value={value}
          onInput={handleChange}
          placeholder={t('profile.stateProvince')}
        />
      );
    }
    return (
      <SearchableSelect
        options={countryStates}
        value={value}
        onChange={(val) => handleSelectChange(fieldName, val)}
        name={fieldName}
        error={!!validationErrors[fieldName]}
        placeholder={t('profile.selectState')}
      />
    );
  };

  if (loading) {
    return <ProfileFormSkeleton />;
  }

  return (
    <div class="sw-tickets-shell">
    <form onSubmit={handleSubmit} noValidate class="sw-surface-card sw-profile-card">
      <div class="row sw-profile-card__grid">
        {/* Left Column: Personal & Billing */}
        <div class="col-md-6 border-end-md">
          <h5 class="sw-profile-card__section-title">{t('profile.personalInfo')}</h5>
          <div class="mb-3">
            <label class="form-label">{t('profile.title')}</label>
            <select class="form-select" name="title" value={formData.title} onChange={handleChange}>
              <option value="Mr.">{t('profile.mr')}</option>
              <option value="Mrs.">{t('profile.mrs')}</option>
              <option value="Ms.">{t('profile.ms')}</option>
            </select>
          </div>
          <div class="mb-3">
            <label class="form-label required">{t('profile.firstName')} <span class="text-danger">*</span></label>
            <input type="text" class={`form-control ${validationErrors.firstname ? 'is-invalid' : ''}`} name="firstname" value={formData.firstname} onInput={handleChange} />
            {validationErrors.firstname && <div class="small text-danger mt-2">{validationErrors.firstname}</div>}
          </div>
          <div class="mb-3">
            <label class="form-label required">{t('profile.lastName')} <span class="text-danger">*</span></label>
            <input type="text" class={`form-control ${validationErrors.lastname ? 'is-invalid' : ''}`} name="lastname" value={formData.lastname} onInput={handleChange} />
            {validationErrors.lastname && <div class="small text-danger mt-2">{validationErrors.lastname}</div>}
          </div>

          <div class="mb-3">
            <label class="form-label required">{t('profile.email')} <span class="text-danger">*</span></label>
            <input type="email" class={`form-control ${validationErrors.email ? 'is-invalid' : ''}`} name="email" value={formData.email} onInput={handleChange} />
            {validationErrors.email && <div class="small text-danger mt-2">{validationErrors.email}</div>}
          </div>
          <div class="mb-3">
            <label class="form-label required">{t('profile.phone')} <span class="text-danger">*</span></label>
            <input type="tel" class={`form-control ${validationErrors.phone ? 'is-invalid' : ''}`} name="phone" value={formData.phone} onInput={handleChange} />
            {validationErrors.phone && <div class="small text-danger mt-2">{validationErrors.phone}</div>}
          </div>

          <h5 class="sw-profile-card__section-title sw-profile-card__section-title--spaced">{t('profile.billingAddress')}</h5>
          <div class="mb-3">
            <label class="form-label required">{t('profile.address')} <span class="text-danger">*</span></label>
            <input type="text" class={`form-control ${validationErrors.b_address ? 'is-invalid' : ''}`} name="b_address" value={formData.b_address} onInput={handleChange} />
            {validationErrors.b_address && <div class="small text-danger mt-2">{validationErrors.b_address}</div>}
          </div>
          <div class="row">
            <div class="col-6 mb-3">
              <label class="form-label required">{t('profile.zipcode')} <span class="text-danger">*</span></label>
              <input type="text" class={`form-control ${validationErrors.b_zipcode ? 'is-invalid' : ''}`} name="b_zipcode" value={formData.b_zipcode} onInput={handleChange} />
              {validationErrors.b_zipcode && <div class="small text-danger mt-2">{validationErrors.b_zipcode}</div>}
            </div>
            <div class="col-6 mb-3">
              <label class="form-label required">{t('profile.city')} <span class="text-danger">*</span></label>
              <input type="text" class={`form-control ${validationErrors.b_city ? 'is-invalid' : ''}`} name="b_city" value={formData.b_city} onInput={handleChange} />
              {validationErrors.b_city && <div class="small text-danger mt-2">{validationErrors.b_city}</div>}
            </div>
          </div>
          <div class="row">
            <div class="col-6 mb-3">
              <label class="form-label required">{t('profile.country')} <span class="text-danger">*</span></label>
              <SearchableSelect
                options={countries}
                value={formData.b_country}
                onChange={(val) => handleSelectChange('b_country', val)}
                name="b_country"
                error={!!validationErrors.b_country}
                placeholder={t('profile.selectCountry')}
                required
              />
              {validationErrors.b_country && <div class="small text-danger mt-2">{validationErrors.b_country}</div>}
            </div>
            <div class="col-6 mb-3">
              <label class="form-label">{t('profile.state')}</label>
              {renderStateSelect(formData.b_country, 'b_state', formData.b_state)}
            </div>
          </div>
        </div>

        {/* Right Column: Shipping & Account */}
        <div class="col-md-6 ps-md-4">
          <h5 class="sw-profile-card__section-title">{t('profile.shippingAddress')}</h5>
          <div class="form-check mb-3">
            <input class="form-check-input" type="checkbox" id="same_as_billing" name="same_as_billing" checked={formData.same_as_billing} onChange={handleChange} />
            <label class="form-check-label" for="same_as_billing">
              {t('profile.sameAsBilling')}
            </label>
          </div>

          {!formData.same_as_billing && (
            <div class="animate-fade-in">
              <div class="mb-3">
                <label class="form-label required">{t('profile.address')} <span class="text-danger">*</span></label>
                <input type="text" class={`form-control ${validationErrors.s_address ? 'is-invalid' : ''}`} name="s_address" value={formData.s_address} onInput={handleChange} />
                {validationErrors.s_address && <div class="small text-danger mt-2">{validationErrors.s_address}</div>}
              </div>
              <div class="row">
                <div class="col-6 mb-3">
                  <label class="form-label required">{t('profile.zipcode')} <span class="text-danger">*</span></label>
                  <input type="text" class={`form-control ${validationErrors.s_zipcode ? 'is-invalid' : ''}`} name="s_zipcode" value={formData.s_zipcode} onInput={handleChange} />
                  {validationErrors.s_zipcode && <div class="small text-danger mt-2">{validationErrors.s_zipcode}</div>}
                </div>
                <div class="col-6 mb-3">
                  <label class="form-label required">{t('profile.city')} <span class="text-danger">*</span></label>
                  <input type="text" class={`form-control ${validationErrors.s_city ? 'is-invalid' : ''}`} name="s_city" value={formData.s_city} onInput={handleChange} />
                  {validationErrors.s_city && <div class="small text-danger mt-2">{validationErrors.s_city}</div>}
                </div>
              </div>
              <div class="row">
                <div class="col-6 mb-3">
                  <label class="form-label required">{t('profile.country')} <span class="text-danger">*</span></label>
                  <SearchableSelect
                    options={countries}
                    value={formData.s_country}
                    onChange={(val) => handleSelectChange('s_country', val)}
                    name="s_country"
                    error={!!validationErrors.s_country}
                    placeholder={t('profile.selectCountry')}
                    required
                  />
                  {validationErrors.s_country && <div class="small text-danger mt-2">{validationErrors.s_country}</div>}
                </div>
                <div class="col-6 mb-3">
                  <label class="form-label">{t('profile.state')}</label>
                  {renderStateSelect(formData.s_country, 's_state', formData.s_state)}
                </div>
              </div>
            </div>
          )}

          <h5 class="sw-profile-card__section-title sw-profile-card__section-title--spaced">{t('profile.changePassword')}</h5>
          <div class="small text-muted mb-3 p-2 border rounded">
            {t('profile.passwordHint')}
          </div>
          <div class="mb-3">
            <label class="form-label">{t('profile.newPassword')}</label>
            <input type="password" class={`form-control ${validationErrors.passwd1 ? 'is-invalid' : ''}`} name="passwd1" value={formData.passwd1} onInput={handleChange} />
            {validationErrors.passwd1 && <div class="small text-danger mt-2">{validationErrors.passwd1}</div>}
          </div>
          <div class="mb-3">
            <label class="form-label">{t('profile.confirmPassword')}</label>
            <input type="password" class={`form-control ${validationErrors.passwd2 ? 'is-invalid' : ''}`} name="passwd2" value={formData.passwd2} onInput={handleChange} />
            {validationErrors.passwd2 && <div class="small text-danger mt-2">{validationErrors.passwd2}</div>}
          </div>

          <div class="mt-5">
            {error && (
              <div class="alert alert-danger py-2 mb-3">
                <span style="display:inline-flex;align-items:center;gap:.4rem">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8" />
                    <path d="M12 7v6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
                    <circle cx="12" cy="16.5" r="1" fill="currentColor" />
                  </svg>
                  {error}
                </span>
              </div>
            )}
            {success && (
              <div class="alert alert-success py-2 mb-3">
                <span style="display:inline-flex;align-items:center;gap:.4rem">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8" />
                    <path d="M8 12.5l2.5 2.5L16 9.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                  {success}
                </span>
              </div>
            )}

            <div class="d-grid">
              <button type="submit" class="btn btn-primary" disabled={saving}>
                {saving ? (
                  <span><span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>{t('profile.saving')}</span>
                ) : (
                  t('profile.updateProfile')
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </form>
    <TelegramPanel variant="account" config={config} />
    </div>
  );
}
